import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, Info, Calendar as CalendarIcon, ClipboardCheck, FileUp, X, Upload, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { SearchableSelect } from "../../components/ui/searchable-select";
import api from "../../utils/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import { cn } from "../../components/ui/utils";

const LEAVE_TYPES = [
  "Annual Leave",
  "Personal Leave",
  "Unpaid Leave"
];

const getLeaveTypeLabel = (type: string) => {
  if (type === "Personal Leave") return "Personal/Sick/Carers Leave";
  return type;
};

const getLeaveTypeDescription = (type: string) => {
  if (type === "Annual Leave") {
    return "Paid time off you can plan ahead for holidays and events";
  }
  if (type === "Personal Leave") {
    return "Otherwise known as sick or carers leave, is paid time off for when you are unexpectedly unwell, injured or caring for an immediate family member";
  }
  if (type === "Unpaid Leave") {
    return "This is unpaid leave — no hours will be deducted from your balance. Finance/HR will be notified for manual adjustment.";
  }
  return "Select a leave type to see more information";
};

// Handover confirmation is required for Annual Leave only. Personal/Sick leave
// and Unpaid Leave (LWOP) submit straight through — no prompt, no checklist.
const HANDOVER_REQUIRED_LEAVE_TYPES = ["Annual Leave"];

// Sentinel for the "Not required — no handover needed" option of the cover
// picker; submitted to the API as coverEmployeeId: null.
const HANDOVER_NOT_REQUIRED = "not-required";

const HANDOVER_NOTES_MAX_LENGTH = 500;

const HANDOVER_CHECKLIST_ITEMS = [
  {
    key: "chk1",
    text: "I have handed over my duties to the person named above, I have spoken to them, and they have confirmed they understand the handover. My team and any other relevant managers know who is covering me.",
    notApplicableWhenNoCover: true
  },
  {
    key: "chk2",
    text: "My out-of-office email reply and voicemail are set, or will be set before my leave starts."
  },
  {
    key: "chk3",
    text: "Any urgent jobs, deadlines or client issues have been flagged and given an owner."
  },
  {
    key: "chk4",
    text: "Meetings in my calendar during this period have been delegated, rescheduled or declined.",
    notApplicableWhenNoCover: true
  },
  {
    key: "chk5",
    text: "No important issue has been left without an owner."
  }
];

const HANDOVER_CONFIRMATION_STATEMENT = "I confirm that I have completed my handover and made sure my responsibilities are properly covered while I am away.";

type HandoverForm = {
  coverEmployeeId: string;
  notes: string;
  checklist: Record<string, boolean>;
};

// Shape of GET /api/users?all_users=true — active, non-archived employees.
type CoverEmployee = {
  id: number | string;
  name: string;
  jobRole?: string | null;
  department?: { id: number | string; departmentName: string } | null;
};

const EMPTY_HANDOVER: HandoverForm = { coverEmployeeId: "", notes: "", checklist: {} };

const RequestLeave = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    leaveType: "Personal Leave",
    hours: "8",
    hoursType: "full",
    customHours: "8",
    startDate: "",
    endDate: "",
    reason: "",
    attachments: [] as (File | { url: string; key: string; filename: string })[],
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showHandoverDialog, setShowHandoverDialog] = useState(false);
  const [handover, setHandover] = useState<HandoverForm>(EMPTY_HANDOVER);
  const [coverEmployees, setCoverEmployees] = useState<CoverEmployee[]>([]);
  const [showInsufficientBalanceDialog, setShowInsufficientBalanceDialog] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [workerInfo, setWorkerInfo] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id } = useParams();
  const isEditMode = !!id;

  const [minAdvanceBooking, setMinAdvanceBooking] = useState(7);
  // Ids of users whose annual-leave requests get the handover dialog (opt-in,
  // Attendance Settings → Handover Dialogue). Absent for everyone by default.
  const [handoverDialogueUserIds, setHandoverDialogueUserIds] = useState<(string | number)[]>([]);
  const [publicHolidays, setPublicHolidays] = useState<any[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<Record<string, { quota: number, used: number, remaining: number }>>({
    "Annual Leave": { quota: 160, used: 0, remaining: 160 },
    "Personal Leave": { quota: 40, used: 0, remaining: 40 }
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/api/attendance/settings');
        if (res.success && res.data) {
          setMinAdvanceBooking(res.data.minAdvanceBookingDays);
          setHandoverDialogueUserIds(
            Array.isArray(res.data.handoverDialogueUserIds) ? res.data.handoverDialogueUserIds : []
          );
        }
      } catch (error) {
        console.error("Error fetching attendance settings:", error);
      }
    };
    const fetchMe = async () => {
      try {
        const res = await api.get('/api/auth/me');
        if (res.success && res.user) {
          setCurrentUser(res.user);
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    const fetchHolidays = async () => {
      try {
        const res = await api.get('/api/attendance/public-holidays');
        if (res.success && res.data) {
          setPublicHolidays(res.data);
        }
      } catch (error) {
        console.error("Error fetching public holidays:", error);
      }
    };

    const fetchCoverEmployees = async () => {
      try {
        const res = await api.get('/api/users?all_users=true');
        if (res.success && res.users) {
          setCoverEmployees(res.users);
        }
      } catch (error) {
        console.error("Error fetching employees for handover cover:", error);
      }
    };

    fetchCoverEmployees();

    fetchSettings();
    fetchMe();
    fetchHolidays();

    if (isEditMode) {
      const fetchLeaveDetails = async () => {
        try {
          setLoading(true);
          const res = await api.get(`/api/attendance/leave-request/${id}`);
          if (res.success && res.data) {
            const leave = res.data;
            const hoursValue = parseFloat(leave.hours).toString();
            setWorkerInfo({
              id: leave.employee.id,
              name: leave.employee.name,
              department: leave.employee.department?.departmentName || "No Department",
              role: leave.employee.jobRole || "No Role",
              standardDailyHours: leave.employee.standardDailyHours
            });
            const standardHours = parseFloat(String(res.data.employee?.standardDailyHours ?? 8));
            const isFullDay = parseFloat(hoursValue) === standardHours;
            const isHalfDay = parseFloat(hoursValue) === standardHours / 2;
            setFormData({
              leaveType: leave.type,
              hours: hoursValue,
              hoursType: isFullDay ? "full" : isHalfDay ? "half" : "custom",
              customHours: hoursValue,
              startDate: leave.startDate,
              endDate: leave.endDate || "",
              reason: leave.reason || "",
              attachments: leave.attachments || [],
            });
            // Amendments re-trigger the handover dialog — pre-fill the recorded
            // responses so the employee only re-confirms them.
            setHandover({
              coverEmployeeId: leave.coverEmployee
                ? String(leave.coverEmployee.id)
                : (leave.handoverConfirmedAt ? HANDOVER_NOT_REQUIRED : ""),
              notes: leave.handoverNotes || "",
              checklist: {
                chk1: !!leave.handoverChk1,
                chk2: !!leave.handoverChk2,
                chk3: !!leave.handoverChk3,
                chk4: !!leave.handoverChk4,
                chk5: !!leave.handoverChk5
              }
            });
          } else {
            toast.error("Failed to fetch leave request details");
            navigate("/attendance/approvals");
          }
        } catch (error) {
          console.error("Error fetching leave details:", error);
          toast.error("An error occurred while fetching request details");
        } finally {
          setLoading(false);
        }
      };
      fetchLeaveDetails();
    }
  }, [id, isEditMode, navigate]);

  useEffect(() => {
    if (!isEditMode && currentUser) {
      const sdh = parseFloat(String(currentUser.standardDailyHours ?? 8));
      setFormData(prev => {
        if (prev.hoursType === "full" && prev.hours === "8") {
          return { ...prev, hours: String(sdh), customHours: String(sdh) };
        }
        return prev;
      });
    }
  }, [currentUser, isEditMode]);

  useEffect(() => {
    const fetchBalances = async () => {
      try {
        let url = '/api/attendance/leave-balance';
        if (isEditMode && workerInfo?.id) {
          url += `?employeeId=${workerInfo.id}`;
        }
        const res = await api.get(url);
        if (res.success && res.data) {
          setLeaveBalances((prev) => ({
            ...prev,
            ...res.data
          }));
        }
      } catch (error) {
        console.error("Error fetching leave balances:", error);
      }
    };

    if ((!isEditMode && currentUser?.id) || (isEditMode && workerInfo?.id)) {
      fetchBalances();
    }
  }, [isEditMode, currentUser?.id, workerInfo?.id]);

  const standardDailyHours = parseFloat(String(
    (isEditMode && workerInfo?.standardDailyHours !== undefined)
      ? workerInfo.standardDailyHours
      : (currentUser?.standardDailyHours ?? 8)
  ));

  const calculateLeaveHours = () => {
    if (!formData.startDate || !formData.leaveType) return 0;

    const hours = parseFloat(formData.hours);
    const start = new Date(formData.startDate);

    if (hours < standardDailyHours) {
      const dayOfWeek = start.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const isHoliday = publicHolidays.some(ph => {
        if (!ph.doNotDeduct) return false;
        const phStart = new Date(ph.startDate);
        const phEnd = new Date(ph.endDate);
        const checkDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const s = new Date(phStart.getFullYear(), phStart.getMonth(), phStart.getDate());
        const e = new Date(phEnd.getFullYear(), phEnd.getMonth(), phEnd.getDate());
        return checkDate >= s && checkDate <= e;
      });

      if (isWeekend || isHoliday) return 0;
      return hours;
    }

    if (!formData.endDate) return 0;

    const end = new Date(formData.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 0;

    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const isHoliday = publicHolidays.some(ph => {
        if (!ph.doNotDeduct) return false;
        const phStart = new Date(ph.startDate);
        const phEnd = new Date(ph.endDate);
        const checkDate = new Date(current.getFullYear(), current.getMonth(), current.getDate());
        const s = new Date(phStart.getFullYear(), phStart.getMonth(), phStart.getDate());
        const e = new Date(phEnd.getFullYear(), phEnd.getMonth(), phEnd.getDate());
        return checkDate >= s && checkDate <= e;
      });

      if (!isWeekend && !isHoliday) {
        count += hours;
      }
      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  const requestedHours = calculateLeaveHours();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleHoursTypeChange = (type: string) => {
    const fullDayHours = String(standardDailyHours);
    const halfDayHours = String(standardDailyHours / 2);
    setFormData((prev) => {
      const newHours = type === "custom" ? prev.customHours : type === "full" ? fullDayHours : halfDayHours;
      return {
        ...prev,
        hoursType: type,
        hours: newHours,
        customHours: type === "custom" ? prev.customHours : newHours,
        // If short leave (< standard daily hours), clear end date to enforce single day
        endDate: parseFloat(newHours) < standardDailyHours ? "" : prev.endDate
      };
    });
  };

  const handleCustomHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormData((prev) => ({
      ...prev,
      customHours: val,
      hours: prev.hoursType === "custom" ? val : prev.hours,
      endDate: parseFloat(val) < standardDailyHours ? "" : prev.endDate
    }));
  };

  const getMinStartDate = (type: string) => {
    const d = new Date();
    d.setDate(d.getDate() + minAdvanceBooking);
    return d.toISOString().split('T')[0];
  };

  const currentMinDate = getMinStartDate(formData.leaveType);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files);
      const validFiles = selected.filter(f => f.size <= 10 * 1024 * 1024);
      if (validFiles.length < selected.length) {
        toast.error("Some files were skipped as they exceed 10MB");
      }
      setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ...validFiles] }));

      // Reset input value so same file can be selected again if removed
      if (e.target) e.target.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, boolean> = {};
    if (!formData.leaveType) newErrors.leaveType = true;
    if (!formData.startDate) newErrors.startDate = true;
    if (!formData.endDate && parseFloat(formData.hours) >= standardDailyHours) newErrors.endDate = true;
    if (!formData.reason.trim()) newErrors.reason = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields indicated by *");
      return;
    }

    // Date Validation
    const selectedStart = new Date(formData.startDate);
    const minAllowedDateStr = getMinStartDate(formData.leaveType);
    const minAllowed = new Date(minAllowedDateStr);

    if (selectedStart < minAllowed) {
      toast.error(`Start date must be at least ${minAdvanceBooking} days in advance (${new Date(minAllowedDateStr).toLocaleDateString()})`);
      setErrors(prev => ({ ...prev, startDate: true }));
      return;
    }

    if (formData.endDate && new Date(formData.endDate) < selectedStart) {
      toast.error("End date cannot be before start date");
      setErrors(prev => ({ ...prev, endDate: true }));
      return;
    }

    // Insufficient balance guard: block when the selected leave type tracks a
    // balance and the user is already negative, or this request would project
    // the balance into the negative. "Unpaid Leave" has no tracked balance,
    // so it is never blocked.
    const selectedBalance = leaveBalances[formData.leaveType];
    if (selectedBalance && (selectedBalance.remaining < 0 || selectedBalance.remaining - requestedHours < 0)) {
      setShowInsufficientBalanceDialog(true);
      return;
    }

    // Build payload and show confirmation dialog
    const payload = {
      leaveType: formData.leaveType,
      hours: formData.hours,
      startDate: formData.startDate,
      endDate: parseFloat(formData.hours) < standardDailyHours ? formData.startDate : formData.endDate,
      reason: formData.reason,
      attachments: formData.attachments
    };

    setPendingPayload(payload);
    setShowConfirmDialog(true);
  };

  // The handover dialog is opt-in per employee (Attendance Settings → Handover
  // Dialogue) and applies to Annual Leave only. It keys off the employee taking
  // the leave — not the viewer — matching the backend.
  const handoverSubjectId = isEditMode ? workerInfo?.id : currentUser?.id;
  const handoverDialogueEnabled = handoverDialogueUserIds.some(id => String(id) === String(handoverSubjectId ?? ''));
  const requiresHandover = handoverDialogueEnabled && HANDOVER_REQUIRED_LEAVE_TYPES.includes(formData.leaveType);

  // The details confirmation hands over to the handover dialog when the
  // employee has it enabled; every other request submits straight through.
  const handleDetailsConfirmed = () => {
    setShowConfirmDialog(false);
    if (requiresHandover) {
      setShowHandoverDialog(true);
    } else {
      handleConfirmedSubmit();
    }
  };

  const handleConfirmedSubmit = async () => {
    if (!pendingPayload) return;

    try {
      setSubmitting(true);
      setShowConfirmDialog(false);

      let uploadedUrls = [];
      const newFiles = formData.attachments.filter(a => a instanceof File) as File[];
      const existingAttachments = formData.attachments.filter(a => !(a instanceof File));

      if (newFiles.length > 0) {
        const uploadData = new FormData();
        newFiles.forEach(file => uploadData.append('files', file));

        const uploadRes = await api.post('/api/attendance/leave-request/upload', uploadData);
        if (uploadRes.success && uploadRes.urls) {
          uploadedUrls = uploadRes.urls;
        }
      }

      const finalAttachments = [...existingAttachments, ...uploadedUrls];

      const coverNotRequired = handover.coverEmployeeId === HANDOVER_NOT_REQUIRED;

      const payload = {
        ...pendingPayload,
        attachments: finalAttachments,
        ...(requiresHandover ? {
          handover: {
            coverEmployeeId: coverNotRequired ? null : Number(handover.coverEmployeeId),
            notes: handover.notes.trim(),
            checklist: {
              chk1: !coverNotRequired && !!handover.checklist.chk1,
              chk2: !!handover.checklist.chk2,
              chk3: !!handover.checklist.chk3,
              chk4: !coverNotRequired && !!handover.checklist.chk4,
              chk5: !!handover.checklist.chk5
            }
          }
        } : {})
      };

      const res = isEditMode
        ? await api.patch(`/api/attendance/leave-request/${id}`, payload)
        : await api.post('/api/attendance/leave-request', payload);

      if (res.success) {
        toast.success(isEditMode ? "Leave request updated successfully" : "Leave request submitted successfully");
        navigate(isEditMode ? "/attendance/approvals" : "/attendance");
      } else {
        toast.error(res.message || `Failed to ${isEditMode ? "update" : "submit"} leave request`);
      }
    } catch (error: any) {
      console.error("Error submitting leave request:", error);
      if (error?.data?.code === 'INSUFFICIENT_LEAVE_BALANCE') {
        setShowInsufficientBalanceDialog(true);
      } else if (typeof error?.data?.code === 'string' && error.data.code.startsWith('HANDOVER_')) {
        setShowHandoverDialog(true);
        toast.error(error.data.message || "Please complete the handover confirmation.");
      } else {
        toast.error(error.response?.data?.message || "An error occurred while submitting your request");
      }
    } finally {
      setSubmitting(false);
      setPendingPayload(null);
    }
  };

  const coverNotRequiredSelected = handover.coverEmployeeId === HANDOVER_NOT_REQUIRED;

  // The person taking leave cannot be their own cover.
  const leaveOwnerId = isEditMode ? workerInfo?.id : currentUser?.id;
  const coverGroups = [{
    heading: "Employees",
    options: coverEmployees
      .filter(u => String(u.id) !== String(leaveOwnerId ?? ""))
      .map(u => ({
        value: String(u.id),
        label: u.name,
        subtitle: [u.jobRole, u.department?.departmentName].filter(Boolean).join(" · ") || undefined
      }))
  }];

  const handoverComplete = handover.coverEmployeeId !== "" && HANDOVER_CHECKLIST_ITEMS.every(item =>
    (!!item.notApplicableWhenNoCover && coverNotRequiredSelected) || !!handover.checklist[item.key]
  );

  // Informational only — short-notice requests are never blocked by this.
  const startDateMs = formData.startDate ? new Date(formData.startDate).getTime() : NaN;
  const isShortNotice = !Number.isNaN(startDateMs) && startDateMs - Date.now() < 48 * 60 * 60 * 1000;

  return (
    <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[32px] border border-gray-100 italic text-gray-400 min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin mb-2" />
          <p>Loading request details...</p>
        </div>
      ) : (
        <>
          {/* Manager Edit Context Banner */}
          {isEditMode && currentUser && workerInfo && currentUser.id !== workerInfo.id && (
            <div className="mb-8 p-4 rounded-[24px] bg-gradient-to-r from-blue-600/10 to-indigo-600/10 border border-blue-100/50 backdrop-blur-md flex items-center gap-4 animate-in slide-in-from-top-4 duration-500">
              <div className="h-12 w-12 rounded-2xl bg-white shadow-sm border border-blue-100 flex items-center justify-center shrink-0">
                <Info className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-600/70 mb-0.5">Manager Edit Context</p>
                <h2 className="text-sm font-bold text-gray-900 leading-tight">
                  Editing Leave Request for <span className="text-blue-700">{workerInfo.name}</span>
                </h2>
                <p className="text-xs text-gray-500 font-medium">
                  {workerInfo.department} • {workerInfo.role}
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="outline"
              size="icon"
              className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
              onClick={() => navigate(isEditMode ? "/attendance/approvals" : "/attendance")}
            >
              <ChevronLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {isEditMode ? "Edit Leave Request" : "Request Leave"}
              </h1>
              <p className="text-gray-500 font-medium text-sm">
                {isEditMode ? "Update your existing leave request" : "Submit a new leave request for approval"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-8">
              <section className="bg-white p-6 sm:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                <div className="mb-2">
                  <h3 className="font-bold text-gray-900 text-lg">Leave Details</h3>
                  <p className="text-gray-500 text-sm font-medium">Fill in the details for your leave request</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Leave Type */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Leave Type *</label>
                    <Select value={formData.leaveType} onValueChange={(val) => handleSelectChange("leaveType", val)}>
                      <SelectTrigger className={cn(
                        "rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all",
                        errors.leaveType && "border-red-500 ring-1 ring-red-500"
                      )}>
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-gray-100">
                        {LEAVE_TYPES.map((type) => (
                          <SelectItem key={type} value={type} className="py-2.5">
                            {getLeaveTypeLabel(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-green-50 border border-green-100 mt-2">
                      <Info className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-green-700 font-medium leading-relaxed">
                        {getLeaveTypeDescription(formData.leaveType)}
                      </p>
                    </div>
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100 mt-2">
                      <Info className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-red-700 font-medium leading-relaxed">
                        Please ensure you have selected the correct leave type by reviewing the leave definitions before submitting your request.
                      </p>
                    </div>
                  </div>

                  {/* Hours selection */}
                  <div className="space-y-3">
                    <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">
                      Hours *
                    </label>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        className={cn(
                          "flex-[2] py-3 rounded-xl border transition-all flex flex-col items-center justify-center",
                          formData.hoursType === "half"
                            ? "border-black bg-black text-white shadow-lg shadow-gray-200"
                            : "border-gray-100 bg-gray-50/50 hover:bg-gray-100/50 text-gray-500"
                        )}
                        onClick={() => handleHoursTypeChange("half")}
                      >
                        <span className="text-base font-bold">Half Day</span>
                        <span className="text-xs opacity-80">{(standardDailyHours / 2).toFixed(2)} hrs</span>
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "flex-[2] py-3 rounded-xl border transition-all flex flex-col items-center justify-center",
                          formData.hoursType === "full"
                            ? "border-black bg-black text-white shadow-lg shadow-gray-200"
                            : "border-gray-100 bg-gray-50/50 hover:bg-gray-100/50 text-gray-500"
                        )}
                        onClick={() => handleHoursTypeChange("full")}
                      >
                        <span className="text-base font-bold">Full Day</span>
                        <span className="text-xs opacity-80">{standardDailyHours.toFixed(2)} hrs</span>
                      </button>
                      <div className="flex-1">
                        <Input
                          type="number"
                          min="0.5"
                          step="0.5"
                          max="24"
                          placeholder="Hrs"
                          value={formData.customHours}
                          onChange={handleCustomHoursChange}
                          onFocus={() => handleHoursTypeChange("custom")}
                          className={cn(
                            "rounded-xl border h-[68px] text-center text-lg font-bold transition-all",
                            formData.hoursType === "custom"
                              ? "border-black bg-white ring-1 ring-black text-gray-900"
                              : "border-gray-100 bg-gray-50/50 text-gray-400"
                          )}
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 font-medium ml-1">Select a preset or enter custom hours. Less than {standardDailyHours.toFixed(2)} hours will be treated as a single day.</p>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Start Date *</label>
                      <div className="relative">
                        <Input
                          type="date"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                          min={currentMinDate}
                          className={cn(
                            "rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all pl-4",
                            errors.startDate && "border-red-500 ring-1 ring-red-500"
                          )}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">
                        End Date {parseFloat(formData.hours) >= standardDailyHours && "*"}
                      </label>
                      <div className="relative">
                        <Input
                          type="date"
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                          min={formData.startDate || currentMinDate}
                          disabled={parseFloat(formData.hours) < standardDailyHours}
                          className={cn(
                            "rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all pl-4",
                            errors.endDate && "border-red-500 ring-1 ring-red-500",
                            parseFloat(formData.hours) < standardDailyHours && "opacity-50 cursor-not-allowed bg-gray-100"
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {formData.endDate && parseFloat(formData.hours) >= standardDailyHours && (
                    <p className="text-[10px] text-gray-400 font-medium ml-1">
                      Your last day of {formData.leaveType.toLowerCase()} leave is {new Date(formData.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}. This is your last day of leave, not your first day back. If this is correct, proceed.
                    </p>
                  )}

                  {/* Reason */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Reason *</label>
                    <Textarea
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      placeholder="Please provide a reason for your leave request..."
                      className={cn(
                        "rounded-xl border-gray-200 bg-gray-50/50 min-h-[120px] focus:bg-white transition-all resize-y",
                        errors.reason && "border-red-500 ring-1 ring-red-500"
                      )}
                    />
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-green-50 border border-green-100 mt-2">
                      <Info className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-green-700 font-medium leading-relaxed">
                        Please describe your leave request in detail. This will be reviewed and approved by your manager
                      </p>
                    </div>
                  </div>

                  {/* Attachment */}
                  <div className="space-y-4">
                    <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Attachments</label>

                    <div className="space-y-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-11 rounded-xl border-gray-200 text-gray-700 bg-gray-50/50 hover:bg-white hover:border-gray-300 font-bold gap-2 border-dashed"
                      >
                        <Upload size={16} />
                        Upload Files
                      </Button>
                      <p className="text-xs text-gray-400 font-medium tracking-wide ml-1">
                        Maximum file size: 10MB.
                      </p>
                      <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                      />

                      {formData.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-3 mt-4">
                          {formData.attachments.map((file, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-100 px-3 py-2 rounded-xl group transition-all hover:border-gray-200">
                              <div className="flex flex-col">
                                <span className="text-xs font-bold text-gray-900 truncate max-w-[150px]">
                                  {file instanceof File ? file.name : file.filename}
                                </span>
                                <span className="text-[10px] text-gray-400 font-medium tracking-tight">
                                  {file instanceof File ? (file.size / (1024 * 1024)).toFixed(2) + " MB" : "Existing File"}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAttachment(idx)}
                                className="p-1 hover:bg-gray-200 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(isEditMode ? "/attendance/approvals" : "/attendance")}
                      className="rounded-xl font-medium px-6 border-gray-200"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="rounded-xl font-medium px-6 bg-black text-white hover:bg-gray-800 disabled:bg-gray-400"
                    >
                      {submitting ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update Request" : "Submit Request")}
                    </Button>
                  </div>
                </form>
              </section>
            </div>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
              <DialogContent className="rounded-[24px] border-gray-200 max-w-md">
                <DialogHeader>
                  <div className="mx-auto sm:mx-0 h-12 w-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-2">
                    <AlertTriangle className="text-amber-600" size={24} />
                  </div>
                  <DialogTitle className="text-xl font-bold text-gray-900">Confirm Your Leave Request</DialogTitle>
                  <DialogDescription className="text-gray-500 text-sm">
                    Please review your selection before submitting. If anything is incorrect, go back and edit.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 my-2">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500 font-medium">Leave Type</span>
                    <span className="text-sm font-bold text-gray-900">{getLeaveTypeLabel(formData.leaveType)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500 font-medium">Hours</span>
                    <span className="text-sm font-bold text-gray-900">{formData.hours} hrs</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500 font-medium">Start Date</span>
                    <span className="text-sm font-bold text-gray-900">
                      {formData.startDate ? new Date(formData.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                    </span>
                  </div>
                  {parseFloat(formData.hours) >= standardDailyHours && formData.endDate && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500 font-medium">End Date</span>
                      <span className="text-sm font-bold text-gray-900">
                        {new Date(formData.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500 font-medium">Total Requested</span>
                    <span className="text-sm font-bold text-gray-900">{requestedHours.toFixed(2)} hrs</span>
                  </div>
                  {leaveBalances[formData.leaveType] && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500 font-medium">Balance After</span>
                      <span className="text-sm font-bold text-red-600">
                        {(leaveBalances[formData.leaveType].remaining - requestedHours).toFixed(4)} hrs
                      </span>
                    </div>
                  )}
                  <div className="py-2">
                    <span className="text-sm text-gray-500 font-medium">Reason</span>
                    <p className="text-sm text-gray-900 font-medium mt-1 leading-relaxed">{formData.reason}</p>
                  </div>
                </div>

                <DialogFooter className="gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowConfirmDialog(false)}
                    className="rounded-xl font-medium px-5 border-gray-200 w-full sm:w-auto"
                  >
                    Go Back & Edit
                  </Button>
                  <Button
                    type="button"
                    onClick={handleDetailsConfirmed}
                    disabled={submitting}
                    className="rounded-xl font-medium px-5 bg-black text-white hover:bg-gray-800 disabled:bg-gray-400 w-full sm:w-auto"
                  >
                    {submitting ? "Submitting..." : "Confirm & Submit"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Handover Confirmation Dialog — Annual Leave only */}
            <Dialog open={showHandoverDialog} onOpenChange={setShowHandoverDialog}>
              <DialogContent className="rounded-[24px] border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <div className="mx-auto sm:mx-0 h-12 w-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-2">
                    <ClipboardCheck className="text-blue-600" size={24} />
                  </div>
                  <DialogTitle className="text-xl font-bold text-gray-900">Before you submit — handover confirmation</DialogTitle>
                  <DialogDescription className="text-gray-500 text-sm">
                    Please confirm the following so your work is covered while you're away.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 my-2">
                  {isShortNotice && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-800 font-bold leading-relaxed">
                        This request is at short notice — please make sure your cover has been briefed today.
                      </p>
                    </div>
                  )}

                  {/* Person covering me */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Person covering me *</label>
                    <SearchableSelect
                      groups={coverGroups}
                      value={handover.coverEmployeeId}
                      onChange={(value) => setHandover(prev => ({ ...prev, coverEmployeeId: value }))}
                      placeholder="Select an employee"
                      searchPlaceholder="Search employees..."
                      emptyMessage="No matching employees."
                      noneOption={{ value: HANDOVER_NOT_REQUIRED, label: "Not required — no handover needed" }}
                    />
                  </div>

                  {/* Notes for cover */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Notes for my cover</label>
                    <Textarea
                      value={handover.notes}
                      onChange={(e) => setHandover(prev => ({ ...prev, notes: e.target.value }))}
                      maxLength={HANDOVER_NOTES_MAX_LENGTH}
                      placeholder="Anything your cover needs to know while you are away..."
                      className="rounded-xl border-gray-200 bg-gray-50/50 min-h-[90px] focus:bg-white transition-all resize-y"
                    />
                    <p className="text-[10px] text-gray-400 font-medium ml-1 text-right">
                      {handover.notes.length}/{HANDOVER_NOTES_MAX_LENGTH}
                    </p>
                  </div>

                  {/* Checklist — every applicable box must be ticked */}
                  <div className="space-y-3">
                    <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Handover checklist *</label>
                    {HANDOVER_CHECKLIST_ITEMS.map((item) => {
                      const notApplicable = !!item.notApplicableWhenNoCover && coverNotRequiredSelected;
                      return (
                        <div
                          key={item.key}
                          className={cn(
                            "flex items-start gap-3 p-3 rounded-xl border transition-all",
                            notApplicable ? "bg-gray-50 border-gray-100 opacity-60" : "bg-white border-gray-200"
                          )}
                        >
                          <Checkbox
                            id={`handover-${item.key}`}
                            checked={!notApplicable && !!handover.checklist[item.key]}
                            disabled={notApplicable}
                            onCheckedChange={(checked) => setHandover(prev => ({
                              ...prev,
                              checklist: { ...prev.checklist, [item.key]: checked === true }
                            }))}
                            className="mt-0.5"
                          />
                          <Label
                            htmlFor={`handover-${item.key}`}
                            className={cn(
                              "text-[11px] leading-relaxed font-medium",
                              notApplicable ? "text-gray-400 cursor-not-allowed" : "text-gray-700 cursor-pointer"
                            )}
                          >
                            {item.text}
                            {notApplicable && <span className="ml-1 italic font-bold">(not applicable)</span>}
                          </Label>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-[10px] text-gray-500 font-medium leading-relaxed italic">
                    {HANDOVER_CONFIRMATION_STATEMENT}
                  </p>
                </div>

                <DialogFooter className="gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowHandoverDialog(false)}
                    className="rounded-xl font-medium px-5 border-gray-200 w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirmedSubmit}
                    disabled={submitting || !handoverComplete}
                    className="rounded-xl font-medium px-5 bg-black text-white hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
                  >
                    {submitting ? "Submitting..." : "Submit Leave Request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Insufficient Balance Disclaimer Dialog */}
            <Dialog open={showInsufficientBalanceDialog} onOpenChange={setShowInsufficientBalanceDialog}>
              <DialogContent className="rounded-[24px] border-gray-200 max-w-md">
                <DialogHeader>
                  <div className="mx-auto sm:mx-0 h-12 w-12 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mb-2">
                    <AlertTriangle className="text-red-600" size={24} />
                  </div>
                  <DialogTitle className="text-xl font-bold text-gray-900">Insufficient Leave Balance</DialogTitle>
                  <DialogDescription className="text-gray-500 text-sm">
                    Unable to submit leave request type due to insufficient balance. Please submit Leave Without Pay.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    type="button"
                    onClick={() => setShowInsufficientBalanceDialog(false)}
                    className="rounded-xl font-medium px-5 bg-black text-white hover:bg-gray-800 w-full sm:w-auto"
                  >
                    OK
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Right Column - Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Leave Balance */}
              <Card className="rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                <CardHeader className="bg-gray-50/50 border-b border-gray-50 pb-4">
                  <CardTitle className="text-lg font-bold">
                    {isEditMode && workerInfo && currentUser?.id !== workerInfo?.id
                      ? `${workerInfo.name}'s Leave Balance`
                      : "Your Leave Balance"}
                  </CardTitle>
                  <CardDescription>Current available leave hours</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {Object.entries(leaveBalances).map(([type, data]) => {
                    const isSelectedType = type === formData.leaveType;
                    const projectedRemaining = isSelectedType ? data.remaining - requestedHours : data.remaining;
                    const projectedUsed = isSelectedType ? data.used + requestedHours : data.used;

                    let colorClass = "bg-green-500";
                    let bgColorClass = "bg-green-100";
                    if (type === "Personal Leave") {
                      colorClass = "bg-blue-500";
                      bgColorClass = "bg-blue-100";
                    } else if (type === "Annual Leave") {
                      colorClass = "bg-green-500";
                      bgColorClass = "bg-green-100";
                    }

                    return (
                      <div key={type} >
                        <div className="flex justify-between items-center text-sm mb-1">
                          <span className={cn("text-gray-600 font-medium", isSelectedType && "text-gray-900 font-bold")}>{getLeaveTypeLabel(type)}</span>
                          <div className="flex flex-col items-end">
                            {isSelectedType && requestedHours > 0 ? (
                              <div className="flex flex-col items-end">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                                  {data.remaining.toFixed(4)} - {requestedHours.toFixed(2)}
                                </span>
                                <span className="font-bold text-red-600">
                                  = {projectedRemaining.toFixed(4)} hours
                                </span>
                              </div>
                            ) : (
                              <span className="font-bold text-gray-900">
                                {data.remaining.toFixed(4)} hours
                              </span>
                            )}
                          </div>
                        </div>
                        <Progress
                          value={(projectedUsed / (data.quota || 1)) * 100}
                          className={cn("h-2", bgColorClass)}
                          indicatorClassName={cn(colorClass, isSelectedType && requestedHours > 0 && "opacity-80")}
                        />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Important Info */}
              <Card className="rounded-[24px] border border-blue-100 bg-blue-50/50 shadow-sm overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4 text-blue-800">
                    <Info className="h-5 w-5" />
                    <h3 className="font-bold text-sm">Important Information</h3>
                  </div>
                  <ul className="space-y-3 text-sm text-blue-800/80">
                    <li className="flex gap-2">
                      <div className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                      <span>Leave requests should be submitted at least {minAdvanceBooking} days in advance</span>
                    </li>
                    <li className="flex gap-2">
                      <div className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                      <span>Manager approval is required for all leave requests</span>
                    </li>
                    <li className="flex gap-2">
                      <div className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                      <span>You will receive an email notification once your request is processed.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Disclaimer */}
              <Card className="rounded-[24px] border border-red-100 bg-red-50/50 shadow-sm overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4 text-red-800">
                    <Info className="h-5 w-5" />
                    <h3 className="font-bold text-sm">Disclaimer</h3>
                  </div>
                  <div className="space-y-3 text-sm text-red-800/80">
                    <p>Unpaid leave, also known as Leave Without Pay (LWOP), is an approved arrangement where an employee takes time off from work without receiving their regular salary. It is commonly used for extended travel, study, or illness once paid leave entitlements are exhausted.</p>
                    <p>Unpaid leave does not count towards your length of continous service and does not accrue paid annual or personal leave.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RequestLeave;
